import { Dispatch, UnknownAction } from "@reduxjs/toolkit";
import BaseApp, { IBaseAppDataDisplayerConfig, IBaseAppDynamicBasePage, IBaseAppFormConfig, IGridDataAdditional, ISearchParam } from "../BaseApp";
import { addNewCategory, deleteCategory, setSelectedCategories, updateAllCategories, updateCategoriesLoadingStatus, updateCategoriesPagination, updateCategoriesSearch, updateCategoriesSearchParams, updateCategory, useReturnAllCategories, useReturnCategoriesLoadingStatus, useReturnCategoriesPagination, useReturnCategoriesSearch, useReturnCategoriesSearchParams, useReturnSelectedCategories } from "./categoriesSlice";
import { GridDataColumns, IGridDataConfig } from "../../components/GridData";
import api from "../../services/api";

/**
 * Section Model
 */
export interface ICategoryModel{
    id: number,
    title: string,
    thumbnail: string,
    created_by: string,
    date_created: string,
    last_updated: string,
    is_active: false,
    is_sub_category: boolean,
    category_id: number,
    sub_categories: Array<ICategoryModel>
}

export const EmptyCategoryModel: ICategoryModel = {
    id: 0,
    title: "",
    thumbnail: "",
    created_by: "",
    date_created: "",
    last_updated: "",
    is_active: false,
    is_sub_category: false,
    category_id: 0,
    sub_categories: []
}

/**
 * Sections App
 */
export default class CategoriesApp extends BaseApp {

    appName = "categories"
    appBaseEndPoint = "/categories"

    // customListEndPoint: string = this.appBaseEndPoint

    messages = {
        create: "Create a new category",
        createShort: "New category",
        edit: "Edit category",
        editShort: "Edit",
        delete: "Delete category",
        deleteShort: "Delete",
        view: "View category",
        nothingFound: "No categories have been found.",
    }

    elemPrefix: string = "categories_"

    dataDisplayer: IBaseAppDataDisplayerConfig = {
        columns: [
            {
                id: "thumbnail",
                type: "thumbnail",
                label: "Thumbnail"
            },
            {
                id: "title",
                type: "text",
                label: "Title"
            },
        ],
        notionFound: {
            title: "Nothing...",
            message: "No categories have been found :("
        }
    }

    formConfig: IBaseAppFormConfig = {
        form: {
            name: "categories_form",
            elements: [
                {
                    id: this.elemPrefix + "title",
                    xs: 8,
                    type: "TextField",
                    label: "Title",
                    fullWidth: true
                },
                {
                    id: this.elemPrefix + "category_id",
                    xs: 4,
                    type: "SelectCategory",
                    label: "Parent Category",
                    fullWidth: true
                },
                {
                    id: this.elemPrefix + "thumbnail",
                    xs: 12,
                    type: "ImageUploader",
                    label: "Thumbnail",
                    fullWidth: true
                },
            ]
        },
        sectionBox: {
            showTitle: true,
            title: "Details",
            description: "Fill in the category details"
        },
        requiredFields: [
            {
                id: this.elemPrefix + "title",
                message: "Title is required!"
            },
        ]
    }

   /**
     * Dynamic base page
     */
    dynamicBasePage: IBaseAppDynamicBasePage = {
        items: useReturnAllCategories,
        pagination: useReturnCategoriesPagination,
        loading: useReturnCategoriesLoadingStatus,
        search: useReturnCategoriesSearch,
        updateLoadingStatus: (status: boolean, dispatch: Dispatch<UnknownAction>) => dispatch(updateCategoriesLoadingStatus({ loading: status })),
        deleteItem: (id: number, dispatch: Dispatch<UnknownAction>) => dispatch(deleteCategory({ id: id })),
        addNewItem: (item, dispatch) => dispatch(addNewCategory((item as ICategoryModel))),
        updateItem: (item, dispatch) => dispatch(updateCategory((item as ICategoryModel))),
        updateAllItems: (items, dispatch) => dispatch(updateAllCategories((items as ICategoryModel[]))),
        updateSearch: (search: string, dispatch: Dispatch<UnknownAction>) => dispatch(updateCategoriesSearch({ search: search })),
        updateItemsPagination: (pagination, dispatch) => dispatch(updateCategoriesPagination(pagination)),
        itemsActionOptions: [
            {
                icon: "edit",
                label: "Edit",
                link: "/panel/categories/edit/[id]",
                onClick: (rawItem) => {
                    // navigate("/panel/items/edit/" + rawItem.id)
                },
            },
            {
                icon: "show",
                label: "View",
                link: "/panel/categories/view/[id]",
                onClick: (rawItem) => {
                    // navigate("/panel/items/view/" + rawItem.id)
                },
            },
        ],
    }

    /**
     * Grid data
     */
    gridDataColumns: GridDataColumns = [
        {
            field: "thumbnail",
            headerName: "Thumbnail",
            type: "thumbnail",
            resizable: false,
            width: 110,
        },
        {
            field: "title",
            headerName: "Title",
            width: 250,
        },
        {
            field: "date_created",
            headerName: "Date Created",
            type: "date",
            width: 250,
        }
    ]
    useGridData: boolean = true
    gridConfig: IGridDataConfig = {
        viewItemLink: "/panel/categories/view/[id]",
        editItemLink: "/panel/categories/edit/[id]",
        hasSubItems: true,
        subItemsParamName: "sub_categories",
    }
    gridDataEssentials: IGridDataAdditional  = {
        selectedItems: useReturnSelectedCategories,
        searchParams: useReturnCategoriesSearchParams,
        actionButtons: [],
        headerOptions: [],
        setSelectedItems: (items, dispatch) => dispatch(setSelectedCategories(items)),
        updateItemSearchParams: (params, dispatch) => dispatch(updateCategoriesSearchParams(params))
    }

    listOfInstances = async(page: number, search: string | null, disablePagination: boolean, addItems?: boolean, searchParams?: Array<ISearchParam>, enableGroups?: boolean) => {

        let path: string = this.appBaseEndPoint + "/"

        if (this.customListEndPoint){
            path = this.customListEndPoint
        }

        path += `?page=${page ? page : 1}`

        if (search !== null){
            path += "&search=" + search
        }

        if (searchParams && searchParams.length > 0){
            searchParams.map((searchParam: ISearchParam) => {
                path += `&${searchParam.name}=${searchParam.value}`
            })
        }

        if (disablePagination){
            path += "&disable_pagination=1"
        }

        if (enableGroups){
            path += "&enable_groups=1"
        }

        return await api.get(path)

    }

}